// Тренировка QA: место, счёт, обратная связь.
//
// Вердикт тренирующегося не попадает ни в индекс, ни в work-воркер, ни в
// отгрузку — только в его собственный набор. Поэтому у ответа на вердикт
// своя форма: он несёт ПРАВИЛЬНЫЙ ответ и настоящую причину из истории,
// без которых тренировка ничему не учит.
import { z } from 'zod';
import { QaTrainingVerdictLessonSchema } from '../vocab/qa-training';
import { UiLangSchema } from '../vocab/ui-lang';

// POST /api/qaTrainingSeat — завести/закрыть место (только владелец ревизии).
export const QaTrainingSeatReqSchema = z.object({
  uid: z.string().trim().min(1).max(64),
  action: z.enum(['open', 'close']),
});
export type QaTrainingSeatReq = z.infer<typeof QaTrainingSeatReqSchema>;
export const QaTrainingSeatResSchema = z.object({
  ok: z.boolean(),
  uid: z.string().default(''),
  seat: z.string().default(''),
  items: z.number().default(0),
});

// POST /api/qaTrainingSeed — пересобрать эталонный набор из истории.
export const QaTrainingSeedResSchema = z.object({
  ok: z.boolean(),
  total: z.number().default(0),
  by_category: z.record(z.string(), z.object({ acc: z.number(), rej: z.number() })).default({}),
  // Пересборка сразу переводит разборы новых формулировок: набор без
  // переводов означает русский разбор у первого же иностранного проверяющего.
  translated: z.number().default(0),
  translate_error: z.string().optional(),
});

// GET /api/getQaTrainingProgress — счёт места.
export const QaTrainingProgressResSchema = z.object({
  training: z.boolean(),
  total: z.number().default(0),
  done: z.number().default(0),
  right: z.number().default(0),
  by_lesson: z.array(z.object({
    lesson: QaTrainingVerdictLessonSchema.catch('other'),
    n: z.number(),
    right: z.number(),
  })).default([]),
});
export type QaTrainingProgressRes = z.infer<typeof QaTrainingProgressResSchema>;

// POST /api/qaTrainingTranslate — перевести разборы эталонного набора.
// Гоняется владельцем после пересборки набора; идемпотентен — переводит
// только то, чего в таблице переводов ещё нет.
export const QaTrainingTranslateResSchema = z.object({
  ok: z.boolean(),
  reasons: z.number().default(0),
  translated: z.number().default(0),
  missing: z.number().default(0),
  error: z.string().optional(),
});
export type QaTrainingTranslateRes = z.infer<typeof QaTrainingTranslateResSchema>;

// POST /api/resetQaTraining — начать заново.
export const QaTrainingResetResSchema = z.object({ ok: z.boolean(), cleared: z.number().default(0) });

// Обратная связь после вердикта в тренировке. Поля необязательные: тот же
// эндпоинт обслуживает и настоящую доску, где никакой обратной связи нет.
export const QaTrainingFeedbackSchema = z.object({
  training: z.boolean().default(false),
  correct: z.boolean().optional(),
  expected_verdict: z.string().optional(),
  // Разбор НА ЯЗЫКЕ ЗАПРОСА (`lang` вердикта). Перевода нет — приходит
  // русский оригинал, приклеенный к названию класса урока на нужном языке:
  // проверяющий в Сан-Паулу должен хотя бы понять, ЧЕМУ карточка учит.
  expected_reason: z.string().optional(),
  // Тот же разбор как он лежит в базе — по-русски, всегда. Владельцу нужен
  // исходник, а не пересказ модели.
  expected_reason_ru: z.string().optional(),
  // На каком языке пришёл expected_reason. 'ru' означает и «перевода нет».
  reason_lang: UiLangSchema.optional(),
  lesson: QaTrainingVerdictLessonSchema.optional(),
  total: z.number().optional(),
  done: z.number().optional(),
  right: z.number().optional(),
});
export type QaTrainingFeedback = z.infer<typeof QaTrainingFeedbackSchema>;
